import { NextResponse } from 'next/server'
import storefrontApi from 'api/storefront-api'
import { parseStoreToBackendOrderLines, paymentShippingFactory } from 'api'
import { openCarrierSession } from '../shippingProviders/carrierSession'
import { persistPaymentId } from './persistPaymentId'
import { resolveSessionCountry } from './resolveSessionCountry'
import {
  type CreateOrderContext,
  type CreateOrderHandlerOpts,
  type CreateOrderRouteResponse,
  type PaymentProviderAdapter
} from './types'

/**
 * The create-order flow, shared by every payment provider.
 *
 * The five providers used to carry four near-identical branch functions each —
 * twenty copies of "parse the body, open a carrier session, create the SF
 * order, build a payload, call the PSP, answer" that differed in four places
 * only. Those four places are the adapter and the carrier session; everything
 * below is the part that was being copied.
 *
 * Order of operations matters in one spot: the freight row is priced *before*
 * the SF order is created, so the row can go into the session payload the
 * provider builds from `backendOrder`. `paymentShippingFactory` is pure, so
 * pricing it early costs nothing.
 */
export const runCreateOrder = async <TSession, TPayload, TCountry extends string>(
  ctx: CreateOrderContext,
  opts: CreateOrderHandlerOpts,
  adapter: PaymentProviderAdapter<TSession, TPayload, TCountry>
): Promise<CreateOrderRouteResponse> => {
  // todo: read `storeId` from `opts` instead of the request body. Every other
  // tenant-scoped value (currency, locale, provider config) comes from the
  // server-side app config; this one lets the client name the store the order
  // is created in. Moving it means adding `storeId` to `CreateOrderHandlerOpts`
  // and passing it from all ten `app/api/orders/create-order/route.ts` files.
  const { storeId, items, cartDiscountCode, countryCode: requestedCountry } = ctx.payload

  const configCountry = adapter.configCountry(opts)
  // No `availableCountries` on the adapter means the session is bound to one
  // market and the shopper's pick is ignored outright — see Nexi.
  const sessionCountry = adapter.availableCountries
    ? resolveSessionCountry(requestedCountry, {
        availableCountries: adapter.availableCountries(opts),
        fallback: configCountry
      })
    : configCountry

  const carrier = await openCarrierSession(ctx, opts)

  // DEFAULT is the only variant without a carrier-owned delivery step, so it is
  // the only one that prices freight here. The shopper's country pick drives
  // which delivery rules match; VAT stays on the merchant's configured country.
  const shippingInfo =
    carrier.provider !== 'DEFAULT'
      ? undefined
      : paymentShippingFactory({
          ...adapter.orderLineFactory(items, cartDiscountCode, configCountry, opts.buyerVat),
          locale: ctx.locale,
          opts: opts.shippingProviderConfig?.simpleShipping,
          shippingConfig: opts.shippingProviderConfig,
          customerType: opts.customerType,
          recipientCountryCode: sessionCountry,
          countryCode: configCountry,
          buyerVat: opts.buyerVat
        })

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: { providerName: adapter.name },
    ...(carrier.shipping ? { shipping: carrier.shipping } : {})
  })

  const payload = await adapter.buildPayload({
    ctx,
    opts,
    backendOrder,
    sessionCountry,
    configCountry,
    shippingInfo
  })

  const { error, data: session } = await adapter.createSession(payload)

  if (error || !session) {
    return NextResponse.json({ error: error || 'Unknown error' }, { status: 400 })
  }

  if (adapter.paymentIdOf) {
    await persistPaymentId(backendOrder, adapter.paymentIdOf(session), backendOrderLines)
  }

  return NextResponse.json({
    storefrontOrderResponse: backendOrder,
    ...adapter.respond(session),
    ...carrier.response
  })
}
